import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { AdminEditColumnStorage } from './admin-edit-column-storage';
import { DefaultColumnInfo, ViewInfo, FormResultType } from './admin-edit-column-info';
import { SelectData, SelectItem } from '../../sample-tracking/reports/interfaces/filter-data';
import { FormControl } from '@angular/forms';
import { AdminEditColumnService } from './admin-edit-column-service';
import { moveItemInArray, transferArrayItem, CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
    selector: 'admin-edit-columns',
    template: require('./edit-columns.component.html'),
    styles: [require('./edit-columns.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditDefaultColumnsComponent {

    public viewList: ViewInfo[];
    public columnInfo: DefaultColumnInfo[];
    public viewData = { isSingleSelect: true, title: 'Views/ Reports' } as SelectData;
    public isLoadingCompleted = false;
    public isDataLoading = true;
    public dropdownError: string = null;
    public isSubmitted = true;
    public searchTerm = new FormControl('');
    public defaultDropDownText = { name: 'Select a View/ Report', id: 0 };
    public defaultColumns: DefaultColumnInfo[]; 
    public availableColumns: DefaultColumnInfo[];
    private selectedView: SelectItem[];
    
    constructor(
        public adminEditStorage: AdminEditColumnStorage,
        public editColumnService: AdminEditColumnService,
        public changeDetectorRef: ChangeDetectorRef,
    ) {
        this.adminEditStorage.getCustomViewList().subscribe((res: ViewInfo[]) => {
            this.isDataLoading = false;
            this.viewList = res;
            this.viewData = {
                ...this.viewData, listItems: res.map((r: ViewInfo) => {
                    return { name: r.viewDisplayName, id: r.naturalKey, data: r };
                }).sort((a, b) => a.name.localeCompare(b.name))
            };
            this.changeDetectorRef.markForCheck();
        });
    }

    public onDropDownChange(event: SelectItem[]) {
        this.selectedView = event;
        this.dropdownError = null;
        this.isLoadingCompleted = false;
        if (event) {
            this.loadColumnInfoByView(event[0].id);
        }
    }

    public drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            if (event.container.id === 'selectedList') {
                let selectedItemId = event.item.element.nativeElement.id;
                let selectedItem = this.availableColumns.find(t => t.appDataColumnDefinitionId === +selectedItemId);
                if (selectedItem && selectedItem.hiddenFlag) { return; }
            }
            if (event.container.id === 'availableList') {
                let selectedItemId = event.item.element.nativeElement.id;
                let selectedItem = this.availableColumns.find(t => t.appDataColumnDefinitionId === +selectedItemId);
                if (selectedItem && selectedItem.hiddenFlag) { return; }
            }
            transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        }
    }

    public onApplyClick() {
        this.isSubmitted = false;
        let data = this.getDefaultAndAvailableColumns(this.columnInfo);
        if (data.defaultColumns && data.defaultColumns.map(t => t.appDataColumnDefinitionId + '-' + t.sequence) ===
                                   this.defaultColumns.map(t => t.appDataColumnDefinitionId + '-' + t.sequence)) { return; }
        let setDefaultColumns = this.defaultColumns.map((dc, index) => {
            return {
                appDataColumnDefinitionId: dc.appDataColumnDefinitionId,
                sequence: index + 1
            };
        });
        let setNonDefaultColumns: any[] = [];
        let nonDefaultColumns = this.availableColumns.filter(t => !data.availableColumns.find(ac => ac.appDataColumnDefinitionId === t.appDataColumnDefinitionId));
        if (nonDefaultColumns && nonDefaultColumns.length) {
            setNonDefaultColumns = nonDefaultColumns.map((ac) => {
                return {
                    appDataColumnDefinitionId: ac.appDataColumnDefinitionId,
                };
            });
        }
        let info = {
            defaultColumns: setDefaultColumns,
            nonDefaultColumns: setNonDefaultColumns
        };
        this.adminEditStorage.saveDefaultColumns(info).subscribe(() => {
            this.loadColumnInfoByView(this.selectedView[0].id);
            this.changeDetectorRef.markForCheck();
        });
    }

    public onResetClick() {
        let data = this.getDefaultAndAvailableColumns(this.columnInfo);
        this.defaultColumns = data.defaultColumns;
        this.availableColumns = data.availableColumns;
        this.changeDetectorRef.markForCheck();
    }

    public getDefaultAndAvailableColumns(data: DefaultColumnInfo[]) {
        let defaultColumns = data.filter((t: DefaultColumnInfo) => t.defaultFlag).map(t => t).sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        let availableColumns = data.filter((t: DefaultColumnInfo) => !t.defaultFlag).map(t => t).sort((a, b) => a.columnDisplayName.localeCompare(b.columnDisplayName));
        return { defaultColumns: defaultColumns, availableColumns: availableColumns };
    }

    public async onAddNewClick() {
        const result = await this.editColumnService.openEditDialog(null).toPromise();
        if (result) {
            if (result.resultType === FormResultType.save) {
                result.columnInfo.appDataDefinitionId = +this.selectedView[0].data.appDataDefinitionId;
                if (result.columnInfo.defaultFlag) {
                    result.columnInfo.sequence = this.defaultColumns.length + 1;
                }
                this.isLoadingCompleted = false;
                this.adminEditStorage.addNewColumn(result.columnInfo).subscribe(() => {
                    this.loadColumnInfoByView(this.selectedView[0].id);
                });
            }
        }
    }

    public async openEditColumnForm(columnInfo: DefaultColumnInfo) {
        const result = await this.editColumnService.openEditDialog(columnInfo).toPromise();
        if (result) {
            this.isLoadingCompleted = false;
            if (result.resultType === FormResultType.save) {
                this.adminEditStorage.saveUpdatedColumnInfo(result.columnInfo).subscribe(() => {
                    this.loadColumnInfoByView(this.selectedView[0].id);
                });
            }
            if (result.resultType === FormResultType.delete) {
                this.deleteColumn(result.columnInfo.appDataColumnDefinitionId);
            }
            if (result.resultType === FormResultType.cancel) {
                this.isLoadingCompleted = true;
                this.changeDetectorRef.markForCheck();
            }
        }
    }

    private loadColumnInfoByView(id: any) {
        this.adminEditStorage.getViewColumnDetailsById(id).subscribe((res: DefaultColumnInfo[]) => {
            this.columnInfo = res;
            let data = this.getDefaultAndAvailableColumns(res);
            this.defaultColumns = data.defaultColumns;
            this.availableColumns = data.availableColumns;
            this.isLoadingCompleted = true;
            this.isSubmitted = true;
            this.changeDetectorRef.markForCheck();
        });
    }

    private  deleteColumn(columnDefinitionId: number) {
        this.isSubmitted = false;
        let defaultList = this.defaultColumns.filter(t => t.appDataColumnDefinitionId !== columnDefinitionId);
        let setDefaultColumns = defaultList.map((dc, index) => {
            return {
                appDataColumnDefinitionId: dc.appDataColumnDefinitionId,
                sequence: index + 1
            };
        });
        let setNonDefaultColumns = [{ appDataColumnDefinitionId: columnDefinitionId }];
        let info = {
            defaultColumns: setDefaultColumns,
            nonDefaultColumns: setNonDefaultColumns
        };
        this.adminEditStorage.saveDefaultColumns(info).subscribe(() => {
            this.adminEditStorage.deleteColumn(columnDefinitionId).subscribe(() => {
                this.loadColumnInfoByView(this.selectedView[0].id);
                this.changeDetectorRef.markForCheck();
            });
        });
    }
}